import type { ScrollViewInstance } from 'react-native';
import { View, Text, StyleSheet, ScrollView, Platform, TouchableOpacity, ActivityIndicator, Animated, useWindowDimensions } from 'react-native';
import { Link, Stack, useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { FontAwesome6 } from '@expo/vector-icons';
import Header from '../Header';
import Footer from '../Footer';
import MetaTags from '../MetaTags';
import Banner from '../ui/Banner';
import { PaperFeedButton } from '../ui/Button';
import ProjectDetailsCard from '../ProjectDetailsCard';
import ProjectOnboardingCard, { type ProjectOnboardingCardState } from './ProjectOnboardingCard';
import ProjectPreviewReadySection from './ProjectPreviewReadySection';
import { useProject } from '../../lib/ProjectContext';
import { activateDiscoveryProject } from '../../lib/discoveryProjectService';
import { useProjectMutations } from '../../lib/hooks/useProjectMutations';
import { useProjectSummariesCount } from '../../lib/hooks/useProjectSummaries';
import { useProjectFeedCount } from '../../lib/hooks/useProjectFeed';
import { useBillingPreviewDigest } from '../../lib/hooks/useBillingPreviewDigest';
import { useCopyAtomFeedUrl } from '../../lib/hooks/useCopyAtomFeedUrl';
import { useCurrentEntitlements } from '../../lib/hooks/useCurrentEntitlements';
import {
    buildPrivateProjectUpgradeMessage,
    buildRssUpgradeMessage,
    canAccessRssDelivery,
    canUsePrivateProjects,
    getRssUpgradeAction,
    isProjectLimitReachedError,
} from '../../lib/entitlements';
import {
    isProjectPublicationConfirmationRequiredError,
    reactivateProjectWithEntitlements,
} from '../../lib/projectReactivation';
import { supabase, toError } from '../../lib/supabase';
import colors from '../../lib/colors';
import { fonts, interSemiBold } from '../../lib/fonts';
import { useFontLoading } from '../../lib/FontContext';
import { isProjectActivated } from '../../lib/projectActivation';
import { buildDashboardPath } from '../../lib/dashboardRoutes';
import { isForwardedOnboardingSource } from '../../lib/forwardedOnboarding';
import {
    buildBillingPreviewTrialHref,
    classifyBillingPreviewProjectAccess,
    getBillingPreviewState,
    isPausedBillingPreviewProject,
} from '../../lib/billingPreviewState';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { type LocalizedMessage, useOptionalResolvedMessage } from '../../lib/i18n/messages';

export default function ProjectSettingsScreen() {
    const { i18n } = useLingui();
    const router = useRouter();
    const pathname = usePathname();
    const {
        digestId,
        onboarding,
        setup_source,
    } = useLocalSearchParams<{ digestId: string; onboarding?: string; setup_source?: string }>();
    const { project, isLoading, error, isRecoveringActivation, refetchProject } = useProject();
    const queryClient = useQueryClient();
    const { showActionSheetWithOptions } = useActionSheet();
    const { deleteProject, isLoading: isDeleting } = useProjectMutations();
    const { width } = useWindowDimensions();
    const entitlementsQuery = useCurrentEntitlements();

    // Responsive breakpoint for action cards grid
    const isNarrow = width <= 600;
    // Scroll refs
    const scrollRef = useRef<ScrollViewInstance>(null);
    const configY = useRef(0);

    // Animation refs
    const fadeAnim = useRef(new Animated.Value(0)).current;
    const slideAnim = useRef(new Animated.Value(20)).current;

    // Animate on mount
    useEffect(() => {
        Animated.parallel([
            Animated.timing(fadeAnim, {
                toValue: 1,
                duration: 400,
                useNativeDriver: Platform.OS !== 'web',
            }),
            Animated.timing(slideAnim, {
                toValue: 0,
                duration: 400,
                useNativeDriver: Platform.OS !== 'web',
            }),
        ]).start();
    }, []);

    // Calculate isProcessing from project.processing_phase
    const processingPhase = project?.processing_phase as string | null;
    const isProcessing = processingPhase !== null &&
                         processingPhase !== 'setup' &&
                         processingPhase !== 'completed';

    // Activation state for email verification flow
    const [isActivating, setIsActivating] = useState(false);
    const [activationError, setActivationError] = useState<LocalizedMessage | null>(null);
    const [activationSuccess, setActivationSuccess] = useState(false);

    // Visibility state
    const [visibility, setVisibility] = useState<'private' | 'public' | 'unlisted'>('private');
    const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);
    const [visibilityError, setVisibilityError] = useState<LocalizedMessage | null>(null);
    const [visibilityUpgradeMessage, setVisibilityUpgradeMessage] = useState<LocalizedMessage | null>(null);

    // Include adjacent papers state
    const [includeAdjacent, setIncludeAdjacent] = useState(true);
    const [isUpdatingIncludeAdjacent, setIsUpdatingIncludeAdjacent] = useState(false);
    const [includeAdjacentError, setIncludeAdjacentError] = useState<LocalizedMessage | null>(null);

    // Pause state
    const [isPaused, setIsPaused] = useState(false);
    const [isUpdatingPaused, setIsUpdatingPaused] = useState(false);
    const [pauseError, setPauseError] = useState<LocalizedMessage | null>(null);
    const [pauseNotice, setPauseNotice] = useState<LocalizedMessage | null>(null);
    const [previewRefreshError, setPreviewRefreshError] = useState<LocalizedMessage | null>(null);
    const [verifiedPausedAccessKey, setVerifiedPausedAccessKey] = useState<string | null>(null);
    // Latest digest type (for fallback banner)
    const [latestDigestType, setLatestDigestType] = useState<string | null>(null);

    // Feed state
    const { fontsLoaded } = useFontLoading();
    const pausedAt = (project as any)?.paused_at as string | null | undefined;
    const pausedAccessKey = digestId && pausedAt ? `${digestId}:${pausedAt}` : null;
    const billingPreviewState = getBillingPreviewState(entitlementsQuery.data);
    const serverPreviewAccess = classifyBillingPreviewProjectAccess(
        entitlementsQuery.data,
        digestId,
        {
            isPending: entitlementsQuery.isPending,
            isChecking: entitlementsQuery.isFetching,
            isError: entitlementsQuery.isError,
        }
    );
    const pausedAccessNeedsVerification = Boolean(
        pausedAccessKey && verifiedPausedAccessKey !== pausedAccessKey
    );
    const previewAccess = serverPreviewAccess === 'restricted'
        ? 'restricted'
        : pausedAccessNeedsVerification
            ? previewRefreshError
                ? 'unavailable'
                : 'checking'
            : serverPreviewAccess;
    const isUiEntitlementPending = previewAccess === 'checking';
    const isUiEntitlementUnavailable = previewAccess === 'unavailable';
    const isRestrictedPreviewProject = previewAccess === 'restricted';
    const previewAccessRetryMessage = previewRefreshError
        ?? (isUiEntitlementUnavailable || (isRestrictedPreviewProject && entitlementsQuery.isError)
            ? msg({ id: 'projectSettings.accessUnavailable', message: 'Paperzilla could not verify this project\'s access. Project controls remain unavailable until you retry.' })
            : null);
    const {
        data: summariesCount = 0,
        isLoading: isSummariesCountLoading,
    } = useProjectSummariesCount(digestId, {
        enabled: !isLoading && Boolean(project) && previewAccess === 'unrestricted',
    });
    const canCopyAtomFeedUrl = previewAccess === 'unrestricted'
        && canAccessRssDelivery(entitlementsQuery.data);
    const rssBlockedMessage = isUiEntitlementPending
        ? msg({ id: 'projectSettings.rssAccessChecking', message: 'Checking your plan. Please try again in a moment.' })
        : isUiEntitlementUnavailable
        ? msg({ id: 'projectSettings.rssAccessUnavailable', message: 'Paperzilla could not verify this project\'s access. Please retry before changing delivery settings.' })
        : buildRssUpgradeMessage(entitlementsQuery.data);
    const rssUpgradeAction = getRssUpgradeAction(entitlementsQuery.data);
    const resolvedRssUpgradeActionLabel = useOptionalResolvedMessage(rssUpgradeAction.label);
    const canMakeProjectPrivate = previewAccess === 'unrestricted'
        && canUsePrivateProjects(entitlementsQuery.data);
    const privateProjectUpgradeMessage = isUiEntitlementPending
        ? msg({ id: 'projectSettings.privateAccessChecking', message: 'Checking your plan. Please try again in a moment.' })
        : isUiEntitlementUnavailable
        ? msg({ id: 'projectSettings.privateAccessUnavailable', message: 'Paperzilla could not verify this project\'s access. Please retry before changing visibility.' })
        : buildPrivateProjectUpgradeMessage(entitlementsQuery.data);
    const {
        copyAtomFeedUrl,
        isCopying: isCopyingAtomFeedUrl,
        copied: atomFeedCopied,
        error: atomFeedError,
        notice: atomFeedNotice,
    } = useCopyAtomFeedUrl({
        canCopy: canCopyAtomFeedUrl,
        blockedMessage: rssBlockedMessage,
    });
    const resolvedAtomFeedNotice = useOptionalResolvedMessage(atomFeedNotice);
    const {
        data: feedTotal = 0,
        isLoading: isFeedTotalLoading,
        refetch: refetchFeedTotal,
    } = useProjectFeedCount(digestId, 'all', 'all', {
        enabled: !isLoading && Boolean(project) && previewAccess === 'unrestricted',
    });
    const hasPersistedBillingPreviewProjectState = isRestrictedPreviewProject;
    const isPersistedPreviewProject = isPausedBillingPreviewProject(entitlementsQuery.data, digestId);
    const rssTrialHref = hasPersistedBillingPreviewProjectState
        ? buildBillingPreviewTrialHref({
            projectId: billingPreviewState.projectId,
            resultDigestId: billingPreviewState.resultDigestId,
        })
        : rssUpgradeAction.path ?? '/pricing';
    const {
        data: previewDigest,
        isLoading: isPreviewPapersLoading,
        isError: isPreviewPapersError,
    } = useBillingPreviewDigest(billingPreviewState.resultDigestId, {
        enabled: isRestrictedPreviewProject && Boolean(billingPreviewState.resultDigestId),
    });
    const previewPapers = previewDigest?.papers ?? [];
    const previousProcessingPhaseRef = useRef<string | null>(null);
    const previewEntitlementRefreshRef = useRef<string | null>(null);
    const previewResultCount = previewDigest?.totalCount ?? previewPapers.length;
    const hasAnyPastResults =
        Boolean(project?.last_digest_sent_at)
        || summariesCount > 0
        || (isRestrictedPreviewProject
            ? previewResultCount > 0 || Boolean(billingPreviewState.resultDigestId)
            : feedTotal > 0);
    const projectActivated = isProjectActivated(project);
    const activationRecoveryPending = Boolean(project) && !projectActivated && isRecoveringActivation;
    const setupNeeded = Boolean(project) && !projectActivated && !activationRecoveryPending;
    const projectFeedPath = digestId
        ? buildDashboardPath(pathname, `/${digestId}`)
        : buildDashboardPath(pathname);
    const projectEditPath = digestId
        ? buildDashboardPath(pathname, `/${digestId}/edit`)
        : buildDashboardPath(pathname);
    const onboardingExperienceActive = Boolean(digestId && onboarding === 'preparing');
    const onboardingSignalsReady = !isSummariesCountLoading
        && (isRestrictedPreviewProject ? !isPreviewPapersLoading : !isFeedTotalLoading);
    const isWaitingForFirstResults = Boolean(project)
        && !(project as any)?.paused_at
        && (projectActivated || activationRecoveryPending)
        && !hasAnyPastResults
        && processingPhase !== 'completed';
    const isForwardedOnboarding = isForwardedOnboardingSource(setup_source);
    const isBillingPreviewOnboardingProject = hasPersistedBillingPreviewProjectState
        && !isForwardedOnboarding;
    const isBillingPreviewAwaitingPause = isBillingPreviewOnboardingProject
        && (billingPreviewState.status === 'configured' || billingPreviewState.status === 'running');
    const isBillingPreviewResultReady = isBillingPreviewOnboardingProject
        && Boolean(billingPreviewState.resultDigestId);
    const showPassivePreparingBanner = !onboardingExperienceActive
        && (isWaitingForFirstResults || (isBillingPreviewAwaitingPause && !isBillingPreviewResultReady));
    const onboardingCardVariant = isForwardedOnboarding
        ? 'forwarded' as const
        : isBillingPreviewOnboardingProject
            ? 'billing-preview' as const
            : 'default' as const;
    let onboardingCardState: ProjectOnboardingCardState | null = null;
    if (onboardingExperienceActive && (projectActivated || activationRecoveryPending)) {
        if (hasAnyPastResults) {
            onboardingCardState = isBillingPreviewAwaitingPause ? 'preparing' : 'ready';
        } else if (processingPhase === 'completed') {
            onboardingCardState = onboardingSignalsReady
                ? 'no-results'
                : 'preparing';
        } else {
            onboardingCardState = 'preparing';
        }
    }
    const isInitialResultsProcessing = onboardingCardState === 'preparing';
    const isPreviewContinuationRequired = Boolean((project as any)?.paused_at)
        && isPersistedPreviewProject
        && !isForwardedOnboarding;
    const showDescriptionOnlyProjectView = (
        isInitialResultsProcessing && !(project as any)?.paused_at
    )
        || isWaitingForFirstResults
        || isBillingPreviewAwaitingPause
        || isPreviewContinuationRequired
        || isUiEntitlementPending
        || isUiEntitlementUnavailable;
    const projectStatusOverride = isPreviewContinuationRequired
        ? { label: i18n._(msg({ id: 'projectSettings.trialNeeded', message: 'Trial needed' })), tone: 'paused' as const }
        : undefined;

    useEffect(() => {
        if (!digestId) {
            previousProcessingPhaseRef.current = null;
            return;
        }

      const previousPhase = previousProcessingPhaseRef.current;
      if (previousPhase && previousPhase !== processingPhase) {
        const transitionedToCompleted = previousPhase !== 'completed' && processingPhase === 'completed';
        if (transitionedToCompleted && previewAccess === 'unrestricted') {
          void refetchFeedTotal();
        }
      }

      previousProcessingPhaseRef.current = processingPhase;
    }, [digestId, previewAccess, processingPhase, refetchFeedTotal]);

    const refreshPreviewAccess = useCallback(async () => {
        setPreviewRefreshError(null);
        try {
            await entitlementsQuery.refreshAuthoritatively();
            if (pausedAccessKey) {
                setVerifiedPausedAccessKey(pausedAccessKey);
            }
        } catch (error) {
            console.error('❌ [Digest] Failed to refresh preview access:', error);
            setPreviewRefreshError(msg({
                id: 'projectSettings.previewRefreshError',
                message: "We could not refresh this preview's access. Your preview remains limited while you retry.",
            }));
        }
    }, [entitlementsQuery.refreshAuthoritatively, pausedAccessKey]);

    useEffect(() => {
        if (!pausedAccessKey) {
            previewEntitlementRefreshRef.current = null;
            return;
        }

        if (previewEntitlementRefreshRef.current === pausedAccessKey) {
            return;
        }

        previewEntitlementRefreshRef.current = pausedAccessKey;
        void refreshPreviewAccess();
    }, [pausedAccessKey, refreshPreviewAccess]);

    useEffect(() => {
        if (!digestId || previewAccess === 'unrestricted') {
            return;
        }

        void Promise.all([
            queryClient.cancelQueries({ queryKey: ['project-feed-count', digestId] }),
            queryClient.cancelQueries({ queryKey: ['projectSummariesCount', digestId] }),
        ]);
    }, [digestId, previewAccess, queryClient]);

    // Initialize visibility, includeAdjacent, and paused state from project
    useEffect(() => {
        if (project) {
            const projectVisibility = project.visibility;
            setVisibility(
                projectVisibility === 'public' || projectVisibility === 'unlisted'
                    ? projectVisibility
                    : 'private'
            );
            setIncludeAdjacent(project.include_adjacent_papers ?? true);
            setIsPaused(!!(project as any).paused_at);
        }
    }, [project]);

    useEffect(() => {
        if (!digestId || !setupNeeded) {
            return;
        }

        router.replace(buildDashboardPath(pathname, `/${digestId}/activate`));
    }, [digestId, pathname, router, setupNeeded]);

    // Fetch latest digest type
    useEffect(() => {
        async function fetchLatestDigest() {
            if (!digestId) return;

            const { data, error } = await supabase
                .from('email_digest')
                .select('digest_type')
                .eq('project_id', digestId)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (!error) {
                setLatestDigestType(data?.digest_type ?? null);
            }
        }
        fetchLatestDigest();
    }, [digestId]);

    // Note: Project activation now happens automatically in ProjectContext
    // The pending_activation flag is set in the database and checked when project loads

    // Retry activation function
    const retryActivation = () => {
        if (!digestId) return;

        console.log('🔄 [Project] Retrying activation:', digestId);
        setIsActivating(true);
        setActivationError(null);

        activateDiscoveryProject(digestId)
            .then(() => {
                console.log('✅ [Project] Retry successful');
                setActivationSuccess(true);
                setTimeout(() => setActivationSuccess(false), 8000);
            })
            .catch(error => {
                console.error('❌ [Project] Retry failed:', error);
                setActivationError(msg({ id: 'projectSettings.activationError', message: 'Failed to activate project' }));
            })
            .finally(() => {
                setIsActivating(false);
            });
    };

    // Handle visibility change
    const handleVisibilityChange = async (newVisibility: 'private' | 'public') => {
        if (!digestId || isUpdatingVisibility) return;

        if (newVisibility === 'private' && !canMakeProjectPrivate) {
            setVisibility('public');
            setVisibilityError(null);
            setVisibilityUpgradeMessage(privateProjectUpgradeMessage);
            return;
        }

        setIsUpdatingVisibility(true);
        setVisibilityError(null);
        setVisibilityUpgradeMessage(null);

        try {
            console.log('🔒 [Digest] Updating visibility to:', newVisibility);

            const { error } = await supabase
                .from('project')
                .update({ visibility: newVisibility })
                .eq('id', digestId)
                .is('deleted_at', null);

            if (error) throw toError(error);

            setVisibility(newVisibility);
            console.log('✅ [Digest] Visibility updated successfully');
        } catch (error: any) {
            console.error('❌ [Digest] Failed to update visibility:', error);
            setVisibilityError(msg({ id: 'projectSettings.visibilityError', message: 'Failed to update visibility' }));
        } finally {
            setIsUpdatingVisibility(false);
        }
    };

    // Handle include adjacent papers change
    const handleIncludeAdjacentChange = async (newValue: boolean) => {
        if (!digestId || isUpdatingIncludeAdjacent) return;

        setIsUpdatingIncludeAdjacent(true);
        setIncludeAdjacentError(null);

        try {
            console.log('📝 [Digest] Updating include_adjacent_papers to:', newValue);

            const { error } = await supabase
                .from('project')
                .update({ include_adjacent_papers: newValue })
                .eq('id', digestId)
                .is('deleted_at', null);

            if (error) throw toError(error);

            setIncludeAdjacent(newValue);
            console.log('✅ [Digest] include_adjacent_papers updated successfully');
        } catch (error: any) {
            console.error('❌ [Digest] Failed to update include_adjacent_papers:', error);
            setIncludeAdjacentError(msg({ id: 'projectSettings.settingError', message: 'Failed to update setting' }));
        } finally {
            setIsUpdatingIncludeAdjacent(false);
        }
    };

    // Handle pause change with confirmation
    const handlePauseProject = () => {
        if (!digestId || isUpdatingPaused) return;

        if (isPaused) {
            performPauseChange(false);
            return;
        }

        const options = [
            i18n._(msg({ id: 'projectSettings.pauseProject', message: 'Pause Project' })),
            i18n._(msg({ id: 'common.cancel', message: 'Cancel' })),
        ];
        const destructiveButtonIndex = 0;
        const cancelButtonIndex = 1;

        showActionSheetWithOptions(
            {
                title: i18n._(msg({ id: 'projectSettings.pauseProject', message: 'Pause Project' })),
                message: i18n._(msg({
                    id: 'projectSettings.pauseConfirmation',
                    message: 'This will stop discovering new papers for this project. Feed updates and digest emails will pause until you resume. If you only want to stop emails, use "Edit Email Delivery" instead.',
                })),
                options,
                cancelButtonIndex,
                destructiveButtonIndex,
            },
            (selectedIndex?: number) => {
                if (selectedIndex === 0) {
                    performPauseChange(true);
                }
            }
        );
    };

    const performPauseChange = async (newValue: boolean, allowMakePublic = false) => {
        if (!digestId || isUpdatingPaused) return;

        setIsUpdatingPaused(true);
        setPauseError(null);
        setPauseNotice(null);

        try {
            console.log('⏸️ [Digest] Updating paused_at to:', newValue ? 'now' : 'null');

            if (!newValue) {
                const result = await reactivateProjectWithEntitlements(digestId, allowMakePublic);
                setVisibility(result.visibility);
                if (result.made_public) {
                    setPauseNotice({
                        ...msg({
                            id: 'projectSettings.resumedPublic',
                            message: 'Project resumed and made public because your current plan does not include private active projects. You now have {active} of {maximum} active projects.',
                        }),
                        values: { active: result.active_projects, maximum: result.max_projects },
                    });
                } else {
                    setPauseNotice({
                        ...msg({
                            id: 'projectSettings.resumed',
                            message: 'Project resumed. You now have {active} of {maximum} active projects; feed updates and digest delivery will start again.',
                        }),
                        values: { active: result.active_projects, maximum: result.max_projects },
                    });
                }
            } else {
                const { error } = await supabase
                    .from('project')
                    .update({ paused_at: new Date().toISOString() })
                    .eq('id', digestId)
                    .is('deleted_at', null);

                if (error) throw toError(error);
            }

            setIsPaused(newValue);
            await Promise.all([
                refetchProject(),
                entitlementsQuery.refreshAuthoritatively(),
            ]);
            console.log('✅ [Digest] paused_at updated successfully');
        } catch (error: any) {
            console.error('❌ [Digest] Failed to update paused_at:', error);
            if (!newValue && isProjectLimitReachedError(error)) {
                setPauseError(msg({
                    id: 'projectSettings.projectLimit',
                    message: 'Your project limit has been reached. Pause another project or upgrade before resuming this one.',
                }));
            } else if (!newValue && isProjectPublicationConfirmationRequiredError(error)) {
                showActionSheetWithOptions(
                    {
                        title: i18n._(msg({ id: 'projectSettings.resumePublicTitle', message: 'Resume as a public project?' })),
                        message: i18n._(msg({
                            id: 'projectSettings.resumePublicMessage',
                            message: 'Your current plan does not include non-public active projects. Resuming this project will make it public; its history and settings remain unchanged.',
                        })),
                        options: [
                            i18n._(msg({ id: 'projectSettings.resumePublic', message: 'Resume as Public' })),
                            i18n._(msg({ id: 'common.cancel', message: 'Cancel' })),
                        ],
                        cancelButtonIndex: 1,
                    },
                    (selectedIndex?: number) => {
                        if (selectedIndex === 0) {
                            performPauseChange(false, true);
                        }
                    },
                );
            } else {
                setPauseError(msg({ id: 'projectSettings.pauseError', message: 'Failed to update pause status' }));
            }
        } finally {
            setIsUpdatingPaused(false);
        }
    };

    // Handle delete project
    const handleDeleteProject = () => {
        if (!digestId || isDeleting) return;

        const options = [
            i18n._(msg({ id: 'projectSettings.deleteProject', message: 'Delete Project' })),
            i18n._(msg({ id: 'common.cancel', message: 'Cancel' })),
        ];
        const destructiveButtonIndex = 0;
        const cancelButtonIndex = 1;

        showActionSheetWithOptions(
            {
                title: i18n._(msg({ id: 'projectSettings.deleteProject', message: 'Delete Project' })),
                message: i18n._(msg({
                    id: 'projectSettings.deleteConfirmation',
                    message: 'Are you sure you want to delete this project? This action cannot be undone. All digest issues, papers, and settings will be permanently deleted.',
                })),
                options,
                cancelButtonIndex,
                destructiveButtonIndex,
            },
            async (selectedIndex?: number) => {
                if (selectedIndex === 0) {
                    try {
                        console.log('🗑️ [Digest] Deleting project:', digestId);
                        await deleteProject(digestId);
                        console.log('✅ [Digest] Project deleted successfully');
                        router.replace(buildDashboardPath(pathname));
                    } catch (error: any) {
                        console.error('❌ [Digest] Failed to delete project:', error);
                    }
                }
            }
        );
    };

    const handleCopyAtomFeedUrl = async () => {
        if (!digestId) return;
        await copyAtomFeedUrl(digestId);
    };

    return (
        <>
            <MetaTags
                title={`${project?.name || 'Project'} - Dashboard - Paperzilla`}
                description="Project overview and management on Paperzilla"
                url={`https://paperzilla.ai/dashboard/${digestId}/settings`}
            />
            <Stack.Screen options={{ headerShown: false }} />
            <View style={styles.page}>
                <Header hidePreviewTrialBanner={isPreviewContinuationRequired} />
                <ScrollView ref={scrollRef} style={styles.scrollView} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
                    <View style={styles.contentWrapper}>
                    <View style={styles.content}>
                        {/* Activation Loading Banner */}
                        {isActivating && (
                            <Banner
                                type="loading"
                                message={msg({ id: 'projectSettings.activating', message: 'Activating your project...' })}
                            />
                        )}

                        {/* Activation Success Banner */}
                        {activationSuccess && !isActivating && (
                            <Banner
                                type="success"
                                message={msg({ id: 'projectSettings.activated', message: "Project activated! You'll receive digest emails as papers are ready." })}
                                dismissable
                            />
                        )}

                        {/* Activation Error Banner */}
                        {activationError && !isActivating && (
                            <Banner
                                type="error"
                                title={msg({ id: 'projectSettings.activationErrorTitle', message: "Couldn't activate project" })}
                                message={activationError}
                                action={{
                                    label: msg({ id: 'common.retry', message: 'Retry' }),
                                    onPress: retryActivation
                                }}
                                dismissable
                            />
                        )}

                        {previewAccessRetryMessage && (
                            <Banner
                                type="error"
                                title={msg({ id: 'projectSettings.accessErrorTitle', message: 'Could not verify project access' })}
                                message={previewAccessRetryMessage}
                                action={{
                                    label: msg({ id: 'common.retry', message: 'Retry' }),
                                    onPress: () => { void refreshPreviewAccess(); },
                                }}
                            />
                        )}

                        {setupNeeded && (
                            <Banner
                                type="warning"
                                title={msg({ id: 'projectSettings.setupIncomplete', message: 'Setup not finished' })}
                                message={msg({ id: 'projectSettings.setupIncompleteMessage', message: 'Your project is created. Confirm ownership so Paperzilla can open the first preview.' })}
                                action={{
                                    label: msg({ id: 'common.continue', message: 'Continue' }),
                                    onPress: () => router.push(buildDashboardPath(pathname, `/${digestId}/activate`)),
                                }}
                            />
                        )}

                        {isPaused && !isPreviewContinuationRequired && (!isUiEntitlementPending || entitlementsQuery.isError) ? (
                            <Banner
                                type="warning"
                                title={msg({ id: 'projectSettings.pausedTitle', message: 'Project Paused' })}
                                message={msg({ id: 'projectSettings.pausedMessage', message: 'This project is paused. We stop discovering new papers, so feed updates and digest emails pause until you resume.' })}
                            />
                        ) : null}

                        {showPassivePreparingBanner && (
                            <Banner
                                type="info"
                                title={isBillingPreviewOnboardingProject
                                    ? msg({ id: 'projectSettings.preparingPreview', message: 'Preparing your one-time preview' })
                                    : msg({ id: 'projectSettings.setupComplete', message: 'Project setup complete' })}
                                message={isBillingPreviewOnboardingProject
                                    ? msg({ id: 'projectSettings.preparingPreviewMessage', message: 'We are still finding your first matching papers in the background. You can leave this page, and we will email your preview when it is ready.' })
                                    : msg({ id: 'projectSettings.preparingResultsMessage', message: "We're still preparing your first results in the background. You can leave this page, and we'll send you a digest email when papers are ready." })}
                            />
                        )}

                        {isBillingPreviewResultReady && digestId ? (
                            <ProjectPreviewReadySection
                                projectId={digestId}
                                projectName={project?.name || i18n._(msg({ id: 'projectSettings.yourProject', message: 'Your project' }))}
                                pathname={pathname}
                                feedHref={projectFeedPath}
                                resultDigestId={billingPreviewState.resultDigestId}
                                totalFoundCount={previewDigest?.totalCount ?? previewPapers.length}
                                papers={previewPapers}
                                isLoading={isPreviewPapersLoading}
                                hasLoadError={isPreviewPapersError}
                                trackingState={billingPreviewState.isPaused ? 'paused' : 'pausing'}
                            />
                        ) : onboardingCardState && digestId ? (
                            <ProjectOnboardingCard
                                state={onboardingCardState}
                                feedHref={projectFeedPath}
                                editHref={projectEditPath}
                                variant={onboardingCardVariant}
                            />
                        ) : null}

                        {/* Pause Error Banner */}
                        {pauseError && (
                            <Banner
                                type="error"
                                title={msg({ id: 'projectSettings.pauseErrorTitle', message: 'Pause Error' })}
                                message={pauseError}
                                dismissable
                            />
                        )}

                        {pauseNotice && (
                            <Banner
                                type="info"
                                title={msg({ id: 'projectSettings.updatedTitle', message: 'Project updated' })}
                                message={pauseNotice}
                                dismissable
                                onDismiss={() => setPauseNotice(null)}
                            />
                        )}

                        <View style={[styles.pageHeader, isNarrow && styles.pageHeaderCompact]}>
                            <Text
                                style={styles.title}
                                accessibilityRole="header"
                                {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 1 })}
                            >
                                {project?.name || <Trans id="digest.projectsettingsscreen.projectOverview" comment="Conditional UI copy in components/digest/ProjectSettingsScreen.tsx">Project Overview</Trans>}
                            </Text>
                            {!showDescriptionOnlyProjectView ? (
                                <Link href={projectFeedPath} asChild>
                                    <PaperFeedButton
                                        variant="primary"
                                        style={styles.primaryActionButton}
                                        textStyle={styles.primaryActionButtonText}
                                        iconSize={14}
                                        activeOpacity={0.7}
                                        testID="project-settings-primary-feed-button"
                                    />
                                </Link>
                            ) : null}
                        </View>

                        {error && (
                            <Banner
                                type="error"
                                title={msg({ id: 'projectSettings.loadErrorTitle', message: 'Error Loading Project' })}
                                message={msg({ id: 'projectSettings.loadErrorMessage', message: 'We could not load this project. Please try again.' })}
                                dismissable
                            />
                        )}

                        {visibilityError && (
                            <Banner
                                type="error"
                                title={msg({ id: 'projectSettings.visibilityErrorTitle', message: 'Visibility Error' })}
                                message={visibilityError}
                                dismissable
                            />
                        )}

                        {visibilityUpgradeMessage && (
                            <Banner
                                type="info"
                                title={msg({ id: 'projectSettings.privateUpgradeTitle', message: 'Private topics are on Plus and Pro' })}
                                message={visibilityUpgradeMessage}
                                dismissable
                                onDismiss={() => setVisibilityUpgradeMessage(null)}
                            />
                        )}

                        {includeAdjacentError && (
                            <Banner
                                type="error"
                                title={msg({ id: 'projectSettings.settingErrorTitle', message: 'Setting Error' })}
                                message={includeAdjacentError}
                                dismissable
                            />
                        )}

                        {resolvedAtomFeedNotice && !canCopyAtomFeedUrl && (
                            <Banner
                                type="info"
                                title={msg({ id: 'projectSettings.rssFeeds', message: 'RSS/Atom feeds' })}
                            >
                                {rssUpgradeAction.kind === 'available' ? (
                                    <Text style={styles.entitlementNoticeText}>
                                        {resolvedAtomFeedNotice}
                                    </Text>
                                ) : rssUpgradeAction.kind === 'trial' ? (
                                    <Text style={styles.entitlementNoticeText}><Trans id="digest.projectsettingsscreen.startA14DayFreeTrialToCopyRssAtomFeedsFor" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">
                                        <Text
                                            style={styles.entitlementNoticeLink}
                                            onPress={() => router.push(rssTrialHref)}
                                            accessibilityRole="link"
                                        >
                                            Start a 14-day free trial
                                        </Text>
                                        {' '}to copy RSS/Atom feeds for this topic. No obligation to buy.
                                    </Trans></Text>
                                ) : rssUpgradeAction.kind === 'legacy_offer' ? (
                                    <Text style={styles.entitlementNoticeText}><Trans id="digest.projectsettingsscreen.yourAccountIsNowOnTheFreePlanToRestore" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">
                                        Your account is now on the Free plan.{' '}
                                        <Text
                                            style={styles.entitlementNoticeLink}
                                            onPress={() => router.push(rssUpgradeAction.path)}
                                            accessibilityRole="link"
                                        >
                                            {resolvedRssUpgradeActionLabel}
                                        </Text>
                                        {' '}to restore RSS/Atom access.
                                    </Trans></Text>
                                ) : (
                                    <Text style={styles.entitlementNoticeText}><Trans id="digest.projectsettingsscreen.rssAtomFeedsAreIncludedWithPaidPlansViewPlansWhen" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">
                                        RSS/Atom feeds are included with paid plans.{' '}
                                        <Text
                                            style={styles.entitlementNoticeLink}
                                            onPress={() => router.push(rssUpgradeAction.path)}
                                            accessibilityRole="link"
                                        >
                                            View plans
                                        </Text>
                                        {' '}when you want to read Paperzilla in your feed reader.
                                    </Trans></Text>
                                )}
                            </Banner>
                        )}

                        {atomFeedError && (
                            <Banner
                                type="error"
                                title={msg({ id: 'projectSettings.rssErrorTitle', message: 'RSS/Atom Link Error' })}
                                message={msg({ id: 'projectSettings.rssErrorMessage', message: 'We could not create the RSS/Atom link. Please try again.' })}
                                dismissable
                            />
                        )}

                        {/* Fallback digest notice */}
                        {latestDigestType === 'fallback' && (
                            <Banner
                                type="info"
                                title={msg({ id: 'projectSettings.suggestedPapers', message: 'Suggested Papers' })}
                                message={msg({ id: 'projectSettings.suggestedPapersMessage', message: 'Your latest digest contains suggested papers because no exact matches were found in the past 7 days.' })}
                            />
                        )}

                        <View style={styles.section} onLayout={(e) => { configY.current = e.nativeEvent.layout.y; }}>
                            <ProjectDetailsCard
                                mode={showDescriptionOnlyProjectView ? 'description' : 'configuration'}
                                visibility={visibility}
                                onVisibilityChange={handleVisibilityChange}
                                isUpdatingVisibility={isUpdatingVisibility}
                                includeAdjacent={includeAdjacent}
                                onIncludeAdjacentChange={handleIncludeAdjacentChange}
                                isUpdatingIncludeAdjacent={isUpdatingIncludeAdjacent}
                                isIncludeAdjacentDisabled={isInitialResultsProcessing}
                                statusOverride={projectStatusOverride}
                            />
                        </View>

                        {!showDescriptionOnlyProjectView ? <Animated.View
                            style={[
                                styles.section,
                                { opacity: fadeAnim, transform: [{ translateY: slideAnim }] }
                            ]}
                        >
                            <Text
                                style={styles.sectionTitle}
                                accessibilityRole="header"
                                {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 2 })}
                            ><Trans id="digest.projectsettingsscreen.actions" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">
                                Actions
                            </Trans></Text>

                            <View style={isNarrow ? styles.actionGridNarrow : styles.actionGrid}>
                                {/* Edit Project - disabled when processing */}
                                {isProcessing ? (
                                    <View style={[styles.actionCard, styles.actionCardDisabled]}>
                                        <View style={styles.actionIconContainer}>
                                            <FontAwesome6 name="pen-to-square" size={18} color={colors.text500} />
                                        </View>
                                        <Text style={[styles.actionTitle, styles.actionTitleDisabled]}><Trans id="digest.projectsettingsscreen.processing" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">Processing...</Trans></Text>
                                        <Text style={styles.actionDescription}><Trans id="digest.projectsettingsscreen.yourProjectIsBeingProcessedEditingWillBeAvailableOnce" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">
                                            Your project is being processed. Editing will be available once processing completes.
                                        </Trans></Text>
                                    </View>
                                ) : (
                                    <TouchableOpacity
                                        style={styles.actionCard}
                                        onPress={() => router.replace(projectEditPath)}
                                        activeOpacity={0.7}
                                    >
                                        <View style={styles.actionIconContainer}>
                                            <FontAwesome6 name="pen-to-square" size={18} color={colors.primaryGreen} />
                                        </View>
                                        <Text style={styles.actionTitle}><Trans id="digest.projectsettingsscreen.editProject" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">Edit Project</Trans></Text>
                                        <Text style={styles.actionDescription}><Trans id="digest.projectsettingsscreen.modifyProjectCriteriaCategoriesInterestsAndKeywords" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">
                                            Modify project criteria: categories, interests, and keywords.
                                        </Trans></Text>
                                    </TouchableOpacity>
                                )}

                                <Link href={buildDashboardPath(pathname, `/${digestId}/delivery?from=edit`)} asChild>
                                    <TouchableOpacity style={styles.actionCard} activeOpacity={0.7}>
                                        <View style={styles.actionCardHeader}>
                                            <View style={styles.actionIconContainer}>
                                                <FontAwesome6 name="envelope" size={18} color={colors.primaryGreen} />
                                            </View>
                                            {project?.email_frequency === 'pause' && (
                                                <View style={styles.pausedBadge}>
                                                    <Text style={styles.pausedBadgeText}><Trans id="digest.projectsettingsscreen.paused" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">Paused</Trans></Text>
                                                </View>
                                            )}
                                        </View>
                                        <Text style={styles.actionTitle}><Trans id="digest.projectsettingsscreen.editEmailDelivery" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">Edit Email Delivery</Trans></Text>
                                        <Text style={styles.actionDescription}><Trans id="digest.projectsettingsscreen.modifyDigestEmailDeliveryOptionsFrequencyMaxPapersPerDigest" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">
                                            Modify digest email delivery options: frequency, max papers per digest, or pause emails.
                                        </Trans></Text>
                                    </TouchableOpacity>
                                </Link>

                                {isInitialResultsProcessing ? (
                                    <View style={[styles.actionCard, styles.actionCardDisabled]} testID="project-settings-paper-digests-disabled">
                                        <View style={styles.actionIconContainer}>
                                            <FontAwesome6 name="newspaper" size={18} color={colors.text500} />
                                        </View>
                                        <Text style={[styles.actionTitle, styles.actionTitleDisabled]}><Trans id="digest.projectsettingsscreen.paperDigests" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">Paper Digests</Trans></Text>
                                        <Text style={styles.actionDescription}><Trans id="digest.projectsettingsscreen.digestHistoryWillAppearAfterYourFirstDigestIsSent" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">
                                            Digest history will appear after your first digest is sent.
                                        </Trans></Text>
                                    </View>
                                ) : (
                                    <Link href={buildDashboardPath(pathname, `/${digestId}/issues`)} asChild>
                                        <TouchableOpacity style={styles.actionCard} activeOpacity={0.7}>
                                            <View style={styles.actionIconContainer}>
                                                <FontAwesome6 name="newspaper" size={18} color={colors.primaryGreen} />
                                            </View>
                                            <Text style={styles.actionTitle}><Trans id="digest.projectsettingsscreen.paperDigests" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">Paper Digests</Trans></Text>
                                            <Text style={styles.actionDescription}><Trans id="digest.projectsettingsscreen.browseAllHistoricalPaperDigestsForThisProject" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">
                                                Browse all historical paper digests for this project.
                                            </Trans></Text>
                                        </TouchableOpacity>
                                    </Link>
                                )}

                                <TouchableOpacity
                                    style={styles.actionCard}
                                    activeOpacity={0.7}
                                    onPress={handleCopyAtomFeedUrl}
                                    disabled={isCopyingAtomFeedUrl}
                                >
                                    <View style={styles.actionIconContainer}>
                                        {isCopyingAtomFeedUrl ? (
                                            <ActivityIndicator size="small" color={colors.primaryGreen} />
                                        ) : (
                                            <FontAwesome6
                                                name={atomFeedCopied ? 'check' : 'rss'}
                                                size={18}
                                                color={atomFeedCopied ? colors.primaryGreenDark : colors.primaryGreen}
                                            />
                                        )}
                                    </View>
                                    <Text style={styles.actionTitle}>
                                        {isCopyingAtomFeedUrl ? <Trans id="digest.projectsettingsscreen.generating" comment="Conditional UI copy in components/digest/ProjectSettingsScreen.tsx">Generating...</Trans> : atomFeedCopied ? <Trans id="digest.projectsettingsscreen.copied" comment="Conditional UI copy in components/digest/ProjectSettingsScreen.tsx">Copied</Trans> : <Trans id="digest.projectsettingsscreen.copyRssAtomUrl" comment="Conditional UI copy in components/digest/ProjectSettingsScreen.tsx">Copy RSS/Atom URL</Trans>}
                                    </Text>
                                    <Text style={styles.actionDescription}><Trans id="digest.projectsettingsscreen.copySecureFeedUrlForFeedlyNetnewswireOrAnyRss" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">
                                        Copy secure feed URL for Feedly, NetNewsWire, or any RSS reader.
                                    </Trans></Text>
                                </TouchableOpacity>

                                {summariesCount > 0 && (
                                    <Link href={buildDashboardPath(pathname, `/${digestId}/summaries`)} asChild>
                                        <TouchableOpacity style={styles.actionCard} activeOpacity={0.7}>
                                            <View style={styles.actionIconContainer}>
                                                <FontAwesome6 name="file-lines" size={18} color={colors.primaryGreen} />
                                            </View>
                                            <Text style={styles.actionTitle}><Trans id="digest.projectsettingsscreen.viewSummaries" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">View Summaries</Trans></Text>
                                            <Text style={styles.actionDescription}><Trans id="digest.projectsettingsscreen.browsePaperWithAiGeneratedSummaries" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">
                                                Browse {summariesCount} paper{summariesCount !== 1 ? 's' : ''} with AI-generated summaries.
                                            </Trans></Text>
                                        </TouchableOpacity>
                                    </Link>
                                )}
                            </View>

                        </Animated.View> : null}

                        {/* Danger Zone */}
                        {!showDescriptionOnlyProjectView ? <View style={styles.section}>
                            <View style={styles.dangerCard}>
                                <Text style={styles.dangerCardTitle}><Trans id="digest.projectsettingsscreen.dangerZone" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">Danger zone</Trans></Text>

                                {!isPreviewContinuationRequired && (
                                    <>
                                        {/* Pause Project */}
                                        <View style={styles.dangerRow}>
                                            <View style={styles.dangerRowLeft}>
                                                <Text style={styles.dangerRowTitle}>
                                                    {isPaused ? <Trans id="digest.projectsettingsscreen.resumeProject" comment="Conditional UI copy in components/digest/ProjectSettingsScreen.tsx">Resume project</Trans> : <Trans id="digest.projectsettingsscreen.pauseProject" comment="Conditional UI copy in components/digest/ProjectSettingsScreen.tsx">Pause project</Trans>}
                                                </Text>
                                                <Text style={styles.dangerRowDescription}>
                                                    {isPaused
                                                        ? <Trans id="digest.projectsettingsscreen.resumeDiscoveringNewPapersAndSendingDigestEmails" comment="Conditional UI copy in components/digest/ProjectSettingsScreen.tsx">Resume discovering new papers and sending digest emails.</Trans>
                                                        : <Trans id="digest.projectsettingsscreen.stopDiscoveringNewPapersFeedUpdatesAndDigestEmailsWill" comment="Conditional UI copy in components/digest/ProjectSettingsScreen.tsx">Stop discovering new papers. Feed updates and digest emails will pause.</Trans>}
                                                </Text>
                                            </View>
                                            <TouchableOpacity
                                                style={[styles.dangerButton, isPaused && styles.dangerButtonResume]}
                                                onPress={handlePauseProject}
                                                disabled={isUpdatingPaused || isProcessing}
                                                activeOpacity={0.7}
                                            >
                                                {isUpdatingPaused ? (
                                                    <ActivityIndicator size="small" color={isPaused ? colors.primaryGreen : '#d97706'} />
                                                ) : (
                                                    <Text style={[styles.dangerButtonText, isPaused && styles.dangerButtonTextResume]}>
                                                        {isPaused ? <Trans id="digest.projectsettingsscreen.resume" comment="Conditional UI copy in components/digest/ProjectSettingsScreen.tsx">Resume</Trans> : <Trans id="digest.projectsettingsscreen.pause" comment="Conditional UI copy in components/digest/ProjectSettingsScreen.tsx">Pause</Trans>}
                                                    </Text>
                                                )}
                                            </TouchableOpacity>
                                        </View>

                                        <View style={styles.dangerDivider} />
                                    </>
                                )}

                                {/* Delete Project */}
                                <View style={styles.dangerRow}>
                                    <View style={styles.dangerRowLeft}>
                                        <Text style={styles.dangerRowTitle}><Trans id="digest.projectsettingsscreen.deleteProject" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">Delete project</Trans></Text>
                                        <Text style={styles.dangerRowDescription}><Trans id="digest.projectsettingsscreen.permanentlyDeleteThisProjectAllDigestIssuesPapersAndSettings" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">
                                            Permanently delete this project, all digest issues, papers, and settings.
                                        </Trans></Text>
                                    </View>
                                    <TouchableOpacity
                                        style={styles.dangerButtonDestructive}
                                        onPress={handleDeleteProject}
                                        disabled={isDeleting}
                                        activeOpacity={0.7}
                                    >
                                        {isDeleting ? (
                                            <ActivityIndicator size="small" color={colors.errorRed} />
                                        ) : (
                                            <Text style={styles.dangerButtonTextDestructive}><Trans id="digest.projectsettingsscreen.delete" comment="UI copy in components/digest/ProjectSettingsScreen.tsx">Delete</Trans></Text>
                                        )}
                                    </TouchableOpacity>
                                </View>
                            </View>
                        </View> : null}
                    </View>
                    </View>
                    <Footer />
                </ScrollView>
                <StatusBar style="auto" />
            </View>
        </>
    );
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
        paddingHorizontal: 0,
        paddingBottom: 0,
    },
    contentWrapper: {
        flex: 1,
    },
    content: {
        width: '100%',
        maxWidth: 800,
        alignSelf: 'center',
        paddingHorizontal: 24,
        paddingTop: 32,
        paddingBottom: 48,
    },
    title: {
        ...fonts.h1,
        flexShrink: 1,
        color: colors.text900,
        letterSpacing: -0.5,
        marginBottom: 28,
    },
    section: {
        marginBottom: 32,
    },
    sectionTitle: {
        ...fonts.bodyMedium,
        fontWeight: '600',
        color: colors.text900,
        letterSpacing: 0.2,
        marginBottom: 16,
    },
    actionGrid: {
        flexDirection: 'row',
        gap: 16,
        flexWrap: 'wrap',
    },
    actionGridNarrow: {
        flexDirection: 'column',
        gap: 16,
    },
    actionCard: {
        backgroundColor: colors.card,
        padding: 20,
        borderRadius: 16,
        minHeight: 140,
        ...(Platform.OS !== 'web' && {
            shadowColor: '#000',
            shadowOpacity: 0.04,
            shadowRadius: 24,
            shadowOffset: { width: 0, height: 4 },
            elevation: 2,
        }),
        flex: 1,
        minWidth: 300,
        ...(Platform.OS === 'web' && {
            boxShadow: '0px 4px 24px rgba(0,0,0,0.04)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
        }),
    },
    actionIconContainer: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: colors.bgWeak,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 14,
    },
    actionTitle: {
        ...fonts.bodyMedium,
        fontWeight: '600',
        color: colors.text900,
        marginBottom: 6,
    },
    actionDescription: {
        ...fonts.bodySmall,
        color: colors.text600,
        lineHeight: 20,
    },
    entitlementNoticeText: {
        ...fonts.bodySmall,
        color: colors.text600,
        lineHeight: 21,
    },
    entitlementNoticeLink: {
        ...fonts.bodySmall,
        fontWeight: '600',
        color: colors.primaryGreenDark,
        lineHeight: 21,
        textDecorationLine: 'underline',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    actionCardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    pausedBadge: {
        backgroundColor: '#fef3c7',
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: 12,
    },
    pausedBadgeText: {
        ...fonts.label,
        fontWeight: '600',
        color: '#d97706',
    },
    actionCardDisabled: {
        opacity: 0.5,
    },
    actionTitleDisabled: {
        color: colors.text500,
    },
    dangerCard: {
        backgroundColor: colors.card,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: colors.errorRed + '30',
        padding: 24,
        ...(Platform.OS !== 'web' && {
            shadowColor: '#000',
            shadowOpacity: 0.04,
            shadowRadius: 24,
            shadowOffset: { width: 0, height: 4 },
            elevation: 2,
        }),
        ...(Platform.OS === 'web' && {
            boxShadow: '0px 4px 24px rgba(0,0,0,0.04)',
        }),
    },
    dangerCardTitle: {
        ...fonts.bodyMedium,
        fontWeight: '600',
        color: colors.errorRed,
        marginBottom: 20,
    },
    dangerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
    },
    dangerRowLeft: {
        flex: 1,
    },
    dangerRowTitle: {
        ...fonts.bodySmall,
        fontWeight: '600',
        color: colors.text900,
        marginBottom: 2,
    },
    dangerRowDescription: {
        ...fonts.label,
        color: colors.text500,
        lineHeight: 18,
    },
    dangerDivider: {
        height: 1,
        backgroundColor: colors.border,
        marginVertical: 16,
    },
    dangerButton: {
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#d97706',
        backgroundColor: 'transparent',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'all 0.15s ease',
        }),
    },
    dangerButtonResume: {
        borderColor: colors.primaryGreen,
    },
    dangerButtonText: {
        ...fonts.bodySmall,
        fontWeight: '600',
        color: '#d97706',
    },
    dangerButtonTextResume: {
        color: colors.primaryGreen,
    },
    dangerButtonDestructive: {
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.errorRed,
        backgroundColor: 'transparent',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'all 0.15s ease',
        }),
    },
    dangerButtonTextDestructive: {
        ...fonts.bodySmall,
        fontWeight: '600',
        color: colors.errorRed,
    },
    pageHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 28,
    },
    pageHeaderCompact: {
        alignItems: 'flex-start',
        flexDirection: 'column',
    },
    primaryActionButton: {
        flexShrink: 0,
        paddingVertical: 12,
        paddingHorizontal: 20,
        borderRadius: 10,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    primaryActionButtonText: {
        fontSize: 14,
    },
});
